import { useCallback, useEffect, useMemo, useState } from "react";
import { UserCog, Loader2, ShieldOff, ShieldCheck, Ban, RotateCcw } from "lucide-react";
import {
  adminListUsers, adminListOrganizations,
  adminSuspendUser, adminDeactivateUser, adminBlockUser, adminReactivateUser,
  formatCommunityPartnerError,
} from "@/services/communityPartnerApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

/**
 * HC-EPIC-004 · Sprint 1-B · HarborWell Community Partner Users admin.
 *
 * Route: `/admin/community-partner-users`
 * Owner directive §6C — minimum controlled interface.
 */
export default function AdminCommunityPartnerUsers() {
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notEnabled, setNotEnabled] = useState(false);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [err, setErr] = useState("");

  // Filters
  const [orgFilter, setOrgFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [busyIds, setBusyIds] = useState({});

  const refresh = useCallback(async () => {
    setErr("");
    try {
      const [u, o] = await Promise.all([
        adminListUsers(orgFilter || undefined),
        adminListOrganizations(),
      ]);
      setUsers(u.items || []);
      setOrgs(o.items || []);
      setNotEnabled(false);
      setNeedsAuth(false);
    } catch (e) {
      const s = e?.response?.status;
      if (s === 404) setNotEnabled(true);
      else if (s === 401 || s === 403) setNeedsAuth(true);
      else setErr(formatCommunityPartnerError(e, "Unable to load Community Partner users."));
    } finally {
      setLoading(false);
    }
  }, [orgFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return users.filter((u) => {
      if (statusFilter && u.status !== statusFilter) return false;
      if (!s) return true;
      return (u.email || "").toLowerCase().includes(s)
          || (u.display_name || "").toLowerCase().includes(s);
    });
  }, [users, search, statusFilter]);

  const runAction = async (u, action) => {
    setBusyIds((b) => ({ ...b, [u.id]: true }));
    try {
      if (action === "suspend") await adminSuspendUser(u.id);
      else if (action === "deactivate") await adminDeactivateUser(u.id);
      else if (action === "block") await adminBlockUser(u.id);
      else if (action === "reactivate") await adminReactivateUser(u.id);
      toast.success("Done.");
      refresh();
    } catch (e) {
      toast.error(formatCommunityPartnerError(e));
    } finally {
      setBusyIds((b) => { const { [u.id]: _, ...rest } = b; return rest; });
    }
  };

  if (loading) return <div className="p-8 text-slate-500 flex items-center gap-2"
                          data-testid="admin-cp-users-loading">
    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
  </div>;
  if (notEnabled) return <div className="p-8 max-w-lg" data-testid="admin-cp-users-not-enabled">
    <Alert><AlertDescription>The Community Partner Portal is not enabled in this environment.</AlertDescription></Alert>
  </div>;
  if (needsAuth) return <div className="p-8 max-w-lg" data-testid="admin-cp-users-needs-auth">
    <Alert variant="destructive"><AlertDescription>You do not have permission to view Community Partner users.</AlertDescription></Alert>
  </div>;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="admin-cp-users-page">
      <div className="flex items-center gap-3">
        <div className="rounded-full bg-slate-900 text-white p-2">
          <UserCog className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-slate-900"
              data-testid="admin-cp-users-title">Community Partner Users</h1>
          <p className="text-sm text-slate-500">HarborWell administration</p>
        </div>
      </div>

      {err && (
        <Alert variant="destructive" data-testid="admin-cp-users-error">
          <AlertDescription>{err}</AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 grid gap-3 md:grid-cols-4"
           data-testid="admin-cp-users-filters">
        <div>
          <Label htmlFor="admin-cp-users-org">Organization</Label>
          <Select value={orgFilter || "all"} onValueChange={(v) => setOrgFilter(v === "all" ? "" : v)}>
            <SelectTrigger id="admin-cp-users-org" data-testid="admin-cp-users-org-trigger">
              <SelectValue placeholder="All organizations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="admin-cp-users-org-all">All organizations</SelectItem>
              {orgs.map((o) => (
                <SelectItem key={o.id} value={o.id}
                            data-testid={`admin-cp-users-org-${o.id}`}>
                  {o.display_name || o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="admin-cp-users-status">Status</Label>
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger id="admin-cp-users-status" data-testid="admin-cp-users-status-trigger">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="Invited">Invited</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Suspended">Suspended</SelectItem>
              <SelectItem value="Deactivated">Deactivated</SelectItem>
              <SelectItem value="Blocked">Blocked</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="admin-cp-users-search">Search email or name</Label>
          <Input id="admin-cp-users-search"
                 data-testid="admin-cp-users-search-input"
                 value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500 bg-slate-50">
            <tr>
              <th className="p-3">Email</th>
              <th className="p-3">Organization</th>
              <th className="p-3">Role</th>
              <th className="p-3">Status</th>
              <th className="p-3">MFA</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="admin-cp-users-tbody">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-slate-400"
                    data-testid="admin-cp-users-empty">
                  No users match the current filters.
                </td>
              </tr>
            )}
            {filtered.map((u) => (
              <tr key={u.id} className="border-t border-slate-100"
                  data-testid={`admin-cp-user-row-${u.id}`}>
                <td className="p-3">
                  <div className="font-medium text-slate-900">{u.email}</div>
                  {u.display_name && <div className="text-xs text-slate-500">{u.display_name}</div>}
                </td>
                <td className="p-3 text-xs text-slate-500">
                  {orgs.find((o) => o.id === u.community_partner_organization_id)?.display_name
                    || u.community_partner_organization_id}
                </td>
                <td className="p-3">{u.role_key}</td>
                <td className="p-3"><Badge variant="outline">{u.status}</Badge></td>
                <td className="p-3">{u.mfa_enrolled ? "Enrolled" : "Pending"}</td>
                <td className="p-3 text-right space-x-1">
                  {u.status === "Active" && (
                    <>
                      <Button size="sm" variant="ghost" disabled={!!busyIds[u.id]}
                              onClick={() => runAction(u, "suspend")}
                              data-testid={`admin-cp-user-suspend-${u.id}`}>
                        <ShieldOff className="w-3.5 h-3.5 mr-1" /> Suspend
                      </Button>
                      <Button size="sm" variant="ghost" disabled={!!busyIds[u.id]}
                              onClick={() => runAction(u, "deactivate")}
                              data-testid={`admin-cp-user-deactivate-${u.id}`}>
                        Deactivate
                      </Button>
                    </>
                  )}
                  {u.status === "Suspended" && (
                    <Button size="sm" variant="ghost" disabled={!!busyIds[u.id]}
                            onClick={() => runAction(u, "reactivate")}
                            data-testid={`admin-cp-user-reactivate-${u.id}`}>
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reactivate
                    </Button>
                  )}
                  {(u.status === "Active" || u.status === "Suspended") && (
                    <Button size="sm" variant="ghost" disabled={!!busyIds[u.id]}
                            onClick={() => runAction(u, "block")}
                            data-testid={`admin-cp-user-block-${u.id}`}>
                      <Ban className="w-3.5 h-3.5 mr-1" /> Block
                    </Button>
                  )}
                  {u.status === "Blocked" && u.blocked_by_harborwell && (
                    <Button size="sm" variant="ghost" disabled={!!busyIds[u.id]}
                            onClick={() => runAction(u, "reactivate")}
                            data-testid={`admin-cp-user-reactivate-blocked-${u.id}`}>
                      <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Reactivate
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
